package components.assemblyPortfolio

import components.editorNameForText
import components.project.ensureElementInList
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.assemblyPortfolio.ResolvedAssemblyPortfolio
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.LizergyCalculationCategories
import it.neckar.lizergy.model.income.IncomePercentageCategory
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.open.collections.fastForEach
import it.neckar.open.time.formatHourAndMinutes
import it.neckar.open.time.formatPersonDays
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import react.*
import react.dom.*
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUsersAndCompanies
import kotlin.time.Duration

internal val PlandatenOverview: FC<PlandatenOverviewProps> = fc("PlandatenOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()
  val companyResolver = useSelectCompanyResolver()
  val usersAndCompanies = useSelectUsersAndCompanies()

  val project = props::project.safeGet()
  val configuration = props::currentConfiguration.safeGet()
  val assemblyPortfolio = props::assemblyPortfolioToSave.getNotNull()

  val montageDachCompany = configuration?.let { companyResolver[configuration.earningsDistribution.montageDach.company] }
  val elektroInstallationCompany = configuration?.let { companyResolver[configuration.earningsDistribution.elektroInstallation.company] }

  val projektLeiter = useState(assemblyPortfolio.value.projektLeiterInformation)
  val pvMontageDurchInformation = useState(assemblyPortfolio.value.pvMontageDurchInformation ?: montageDachCompany)
  val ansprechpartnerDachInformation = useState(assemblyPortfolio.value.ansprechpartnerDachInformation)
  val elektroMontageDurchInformation = useState(assemblyPortfolio.value.elektroMontageDurchInformation ?: elektroInstallationCompany)
  val ansprechpartnerElektroInformation = useState(assemblyPortfolio.value.ansprechpartnerElektroInformation)


  useEffect(
    projektLeiter.value,
    pvMontageDurchInformation.value,
    ansprechpartnerDachInformation.value,
    elektroMontageDurchInformation.value,
    elektroInstallationCompany,
    ansprechpartnerElektroInformation.value
  ) {
    assemblyPortfolio.setter {
      it.copy(
        projektLeiterInformation = projektLeiter.value,
        pvMontageDurchInformation = if (pvMontageDurchInformation.value != montageDachCompany) pvMontageDurchInformation.value else null,
        ansprechpartnerDachInformation = ansprechpartnerDachInformation.value,
        elektroMontageDurchInformation = if (elektroMontageDurchInformation.value != elektroInstallationCompany) elektroMontageDurchInformation.value else null,
        ansprechpartnerElektroInformation = ansprechpartnerElektroInformation.value,
      )
    }
  }


  div("row my-4") {
    h3("my-3") { +"Plandaten" }

    div("col-lg-6") {
      div("mb-3") {
        floatingSelectNullable(
          valueAndSetter = projektLeiter,
          idProvider = { it?.loginName?.value ?: "NONE" },
          formatter = { it?.editorNameForText() ?: "-" },
          fieldName = "projektLeiter",
          title = "Projektleiter",
          availableOptionsWithoutNull = usersAndCompanies.usersForCompany(project.sellingCompany).ensureElementInList(projektLeiter.value),
        )
      }

      div("row gx-0") {
        div("col-lg-6") {
          floatingSelectNullable(
            valueAndSetter = pvMontageDurchInformation,
            idProvider = { it?.companyCode?.value ?: "NONE" },
            formatter = { it?.name ?: "-" },
            fieldName = "pvMontageDurch",
            title = "PV-Montage",
            availableOptionsWithoutNull = usersAndCompanies.activeCompanies(loggedInUser),
          ) {
            attrs {
              mergedRight()
              mergedBelow()
            }
          }
        }
        div("col-lg-6") {
          floatingSelectNullable(
            valueAndSetter = ansprechpartnerDachInformation,
            idProvider = { it?.loginName?.value ?: "NONE" },
            formatter = { it?.editorNameForText() ?: "-" },
            fieldName = "ansprechpartnerDach",
            title = "Ansprechpartner Dach",
            availableOptionsWithoutNull = (pvMontageDurchInformation.value?.let { usersAndCompanies.usersForCompany(it.companyCode) } ?: emptyList()).ensureElementInList(ansprechpartnerDachInformation.value),
          ) {
            attrs {
              mergedLeft()
              mergedBelow()
            }
          }
        }
        div("col-lg-6") {
          floatingSelectNullable(
            valueAndSetter = elektroMontageDurchInformation,
            idProvider = { it?.companyCode?.value ?: "NONE" },
            formatter = { it?.name ?: "-" },
            fieldName = "elektroMontageDurch",
            title = "Elektromontage",
            availableOptionsWithoutNull = usersAndCompanies.activeCompanies(loggedInUser),
          ) {
            attrs {
              mergedRight()
              mergedAbove()
            }
          }
        }
        div("col-lg-6") {
          floatingSelectNullable(
            valueAndSetter = ansprechpartnerElektroInformation,
            idProvider = { it?.loginName?.value ?: "NONE" },
            formatter = { it?.editorNameForText() ?: "-" },
            fieldName = "ansprechpartnerElektro",
            title = "Ansprechpartner Elektro",
            availableOptionsWithoutNull = (elektroMontageDurchInformation.value?.let { usersAndCompanies.usersForCompany(it.companyCode) } ?: emptyList()).ensureElementInList(ansprechpartnerElektroInformation.value),
          ) {
            attrs {
              mergedLeft()
              mergedAbove()
            }
          }
        }
      }
    }

    if (configuration != null) {
      val quoteElements = configuration.calculatedQuoteElements
      div("col-lg-6") {
        div("form-text my-3") {
          h4("mb-2") { +"Arbeitszeiten Abschätzung" }
          IncomePercentageCategory.entries.fastForEach { incomePercentageCategory ->
            val query = LizergyCalculationCategories.Query.Profits[incomePercentageCategory]
            val totalWorkingTime = quoteElements.workingTimes(query)
            if (totalWorkingTime > Duration.ZERO) {
              p {
                +"${incomePercentageCategory.description}: ${totalWorkingTime.formatHourAndMinutes()}"
              }
            }
          }
          p {
            b {
              val totalWorkingTime = quoteElements.workingTimes()
              +"Gesamt: ${totalWorkingTime.formatHourAndMinutes()} / ${totalWorkingTime.formatPersonDays()}"
            }
          }
        }
      }
    }
  }
}

external interface PlandatenOverviewProps : Props {
  var project: ResolvedProject
  var currentConfiguration: QuoteConfiguration?
  var assemblyPortfolioToSave: StateInstance<ResolvedAssemblyPortfolio>
}
